import { Theme, styled } from "@mui/material/styles";
import { Box, Typography } from "@mui/material";
import {
  FormControl as MuiFormControl,
  Select as MuiSelect,
} from "@material-ui/core";

/** Outer container for the modal body — flex row of 2 panes. */
export const StyledModalBody = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(3),
  minHeight: "520px",
  width: "100%",
}));

/** Left configuration pane. */
export const StyledConfigPane = styled(Box)(({ theme }) => ({
  flex: "0 0 280px",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2.5),
  paddingRight: theme.spacing(2),
  borderRight: `1px solid ${theme.palette.divider}`,
}));

/** Right planogram pane. */
export const StyledPlanogramPane = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  minWidth: 0,
}));

/** A labeled section block in the config pane. */
export const StyledConfigSection = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  "& .heatmap-section-label": {
    marginBottom: theme.spacing(0.5),
  },
}));

/**
 * Color swatch button — the small high/low color squares in the gradient picker.
 * Matches legacy `#highest-value-color, #lowest-value-color` — 20×20 square
 * with a thin 1px black border.
 */
export const StyledSwatchButton = styled("button", {
  shouldForwardProp: (prop) => prop !== "swatchColor" && prop !== "isActive",
})<{ swatchColor: string; isActive?: boolean }>(
  ({ theme, swatchColor, isActive }) => ({
    width: "20px",
    height: "20px",
    minWidth: "20px",
    border: `1px solid ${
      isActive ? theme.palette.primary.main : theme.palette.text.primary
    }`,
    borderRadius: 0,
    backgroundColor: swatchColor,
    cursor: "pointer",
    padding: 0,
    transition: "border-color 120ms ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
    },
  }),
);

/**
 * Container for the expanded gradient swatch palette.
 * Matches legacy ColorPicker: 8 colors in a single row, 25×25 cells, no gaps,
 * no border-radius.
 */
export const StyledSwatchPalette = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  gap: 0,
  marginTop: theme.spacing(1),
  padding: "5px",
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  width: "fit-content",
}));

export const StyledPaletteSwatch = styled("button", {
  shouldForwardProp: (prop) => prop !== "swatchColor" && prop !== "isSelected",
})<{ swatchColor: string; isSelected?: boolean }>(
  ({ theme, swatchColor, isSelected }) => ({
    width: "25px",
    height: "25px",
    border: `2px solid ${
      isSelected ? theme.palette.primary.main : "transparent"
    }`,
    borderRadius: 0,
    backgroundColor: swatchColor,
    cursor: "pointer",
    padding: 0,
    boxSizing: "border-box",
    "&:hover": {
      borderColor: theme.palette.primary.main,
    },
  }),
);

export const StyledPlanogramGrid = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "columns" && prop !== "sideLength" && prop !== "cellMargin",
})<{ columns: number; sideLength: number; cellMargin: number }>(
  ({ columns, sideLength, cellMargin }) => ({
    display: "grid",
    gridTemplateColumns: `repeat(${columns}, ${sideLength}px)`,
    gap: `${cellMargin * 2}px`,
    justifyContent: "flex-start",
    alignContent: "start",
    padding: 0,
    paddingTop: 8,
    border: "none",
    backgroundColor: "transparent",
  }),
);

/**
 * Wrapping flex row of P# page-columns. Mirrors legacy
 * `.heat-map-page-container { float: left; margin-right: 20px; margin-bottom: 20px }`
 * — page-columns flow horizontally and wrap to a second row when the
 * available width runs out.
 */
export const StyledPageColumns = styled(Box)({
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  gap: "20px",
  alignItems: "flex-start",
  marginTop: "10px",
});

/** A single P# vertical block (label on top + sub-grid below). */
export const StyledPageColumn = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "4px",
});

/** "P#N" label above each page-column. */
export const StyledPageColumnLabel = styled("label")(({ theme }) => ({
  fontFamily: "Helvetica",
  fontSize: "12px",
  fontWeight: 700,
  color: theme.palette.text.secondary,
}));

/**
 * Sub-grid for a page (≤24 cells laid out in `columns` from ViewContext).
 * Same logic as `StyledPlanogramGrid` but specifically for the per-page
 * grouping inside `StyledPageColumns`.
 */
export const StyledPageGrid = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "columns" && prop !== "sideLength" && prop !== "cellMargin",
})<{ columns: number; sideLength: number; cellMargin: number }>(
  ({ columns, sideLength, cellMargin }) => ({
    display: "grid",
    gridTemplateColumns: `repeat(${columns}, ${sideLength}px)`,
    gap: `${cellMargin * 2}px`,
    justifyContent: "flex-start",
    alignContent: "start",
    padding: 0,
    border: "none",
    backgroundColor: "transparent",
  }),
);

export const StyledPlanogramCell = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "cellColor" && prop !== "sideLength" && prop !== "borderRadius",
})<{ cellColor?: string; sideLength: number; borderRadius: number }>(
  ({ theme,cellColor, sideLength, borderRadius }) => ({
    width: `${sideLength}px`,
    height: `${sideLength}px`,
    backgroundColor: cellColor ?? theme.palette.neutral.white,
    border: "1px solid #c0c0c0",
    borderRadius: `${borderRadius}px`,
    boxSizing: "border-box",
    display: "inline-block",
  }),
);

export const StyledPlanogramBody = styled(Box)({
  display: "flex",
  flexDirection: "row",
  flex: 1,
  minHeight: 0,
  alignItems: "flex-start",
  gap: "60px",
});

export const StyledPlanogramGridWrapper = styled(Box)({
  flex: 0.8,
  minWidth: 0,
});

export const StyledColorScale = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 0,
  flex: "0 0 auto",
  paddingTop: "8px",
});

export const StyledColorScaleBand = styled(Box, {
  shouldForwardProp: (prop) => prop !== "bandColor",
})<{ bandColor: string }>(({ bandColor }) => ({
  position: "relative",
  height: "30px",
  width: "10px",
  backgroundColor: bandColor,
}));

/** Absolutely-positioned label that sits to the right of each scale band. */
export const StyledColorScaleValue = styled(Typography)(({ theme }) => ({
  position: "absolute",
  left: "15px",
  top: "-2px",
  whiteSpace: "nowrap",
}));

export const StyledModalTitle = styled(Box)(({ theme }) => ({
  fontFamily: "Helvetica",
  fontSize: "18px",
  fontWeight: 700,
  color: theme.palette.text.primary,
}));

export const dateRangeButtonStyles = (theme: Theme) => ({
  width: "100%",
  justifyContent: "space-between",
  textTransform: "none",
  fontFamily: "Helvetica",
  fontSize: "13px",
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1, 1.5),
  color: theme.palette.text.primary,
  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor: "transparent",
  },
});

export const StyledHeatMapFormControl = styled(MuiFormControl)(({ theme }) => ({
  width: "100%",
  marginTop: "12px",
  marginBottom: 2,
  "& .MuiInputLabel-outlined": {
    fontSize: "16px",
    lineHeight: "22.4px",
    fontWeight: 400,
    fontFamily: "Helvetica",
    color: theme.palette.text.secondary,
  },
  "& .MuiInputLabel-outlined.Mui-focused": {
    color: theme.palette.focusColor?.main ?? theme.palette.primary.main,
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.focusColor?.main ?? theme.palette.primary.main,
    borderWidth: "1.5px",
  },
}));

export const StyledHeatMapSelect = styled(MuiSelect)(({ theme }) => ({
  width: "100%",
  maxHeight: 50,
  backgroundColor: theme.palette.background.paper,
  fontFamily: "Helvetica",
  fontSize: "16px",
  "& .MuiOutlinedInput-input, & select.MuiSelect-select, & select": {
    fontFamily: "Helvetica",
    fontSize: "16px",
    height: "50.4px",
    boxSizing: "border-box",
    paddingTop: "16px",
    paddingBottom: "12px",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.divider,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.text.primary,
  },
}));

export const heatMapModalStyles = () => {
  return {
    colorBox: {
      display: "flex",
      flexDirection: "column",
      gap: 1
    },
    valueBox: {
      display: "flex",
      gap: 1.5,
      alignItems: "center",
    },
    valueLabel: {
      flex: 1,
    },
    dateRangePicker: { 
      display: "flex",
      gap: 1,
      mt: 1,
    },
    planogramBox: {
      minWidth: "120px",
      marginTop: "20px",
    },
    colorScaleBox: {
      position: "relative",
      width: "10px",
      height: 0,
    },
    noProductsBox: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: 1,
      color: "text.secondary",
      fontFamily: "Helvetica",
    }
  }};
